import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Step {
  id: string;
  label: string;
  status: 'waiting' | 'in-progress' | 'complete' | 'error';
  substep?: string | null;
  duration?: number;
}

interface ResearchProgressStepperProps {
  steps: Step[];
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = (ms / 1000).toFixed(1);
  return `${seconds}s`;
}

export function ResearchProgressStepper({ steps }: ResearchProgressStepperProps) {
  return (
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center gap-3">
          <div className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full border-2",
            step.status === 'complete' && "bg-green-500 border-green-500",
            step.status === 'in-progress' && "bg-primary border-primary animate-pulse",
            step.status === 'waiting' && "bg-muted border-muted-foreground/20",
            step.status === 'error' && "bg-destructive border-destructive"
          )}>
            {step.status === 'complete' && <CheckCircle className="h-5 w-5 text-white" />}
            {step.status === 'in-progress' && <Loader2 className="h-5 w-5 text-primary-foreground animate-spin" />}
            {step.status === 'waiting' && <span className="text-xs text-muted-foreground">{index + 1}</span>}
            {step.status === 'error' && <AlertTriangle className="h-5 w-5 text-destructive-foreground" />}
          </div>

          <div className="flex-1">
            <div className="font-medium text-foreground">{step.label}</div>
            {step.substep && step.status === 'in-progress' && (
              <div className="text-sm text-muted-foreground italic mt-1 animate-pulse">
                → {step.substep}
              </div>
            )}
            {step.duration !== undefined && (
              <div className="text-xs text-muted-foreground">
                Completed in {formatDuration(step.duration)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
